import React, { useState, useMemo } from 'react'
import { TitleDivider } from 'components/TitleDivider'
import { VideoDialog } from 'components/VideoDialog'
import { VideoPlayer } from 'components/VideoPlayer'
import Section from 'components/Section'
import { useMemoizedContentGetter } from '@/utils/contentful'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { StatGrid } from 'components/StatGrid'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { AchieveLink } from '@/components/AchieveLink'
import { withUuid } from '@/utils/shared'

export const TestimonialTabs = ({ content }) => {
  const { eyebrow, title, memberStories, iconSvgs, backgroundConfig } = useMemoizedContentGetter(
    content,
    ['eyebrow', 'title', 'memberStories', 'iconSvgs', 'backgroundConfig']
  )

  const getInitialIndex = () => {
    if (typeof window === 'undefined') return 0
    const hash = window.location.hash.slice(1) // Remove the '#' prefix
    if (!hash) return 0

    const matchingIndex = memberStories?.gridItemsCollection?.items?.findIndex(
      (item) => item?.config?.id === hash
    )

    return matchingIndex !== -1 ? matchingIndex : 0
  }

  const [selected, setSelected] = useState(getInitialIndex)
  const tabs = [0, 1, 2]

  const handleClick = (index) => setSelected(index)

  const handlePrev = () => setSelected((prev) => (prev === 0 ? tabs.length - 1 : prev - 1))
  const handleNext = () => setSelected((prev) => (prev === tabs.length - 1 ? 0 : prev + 1))

  const story = memberStories?.gridItemsCollection?.items?.[selected]

  const tabSvgs = iconSvgs?.gridItemsCollection?.items
  const storyContent = useMemo(() => withUuid(story?.message?.json?.content), [story])
  const { useVariantA = false } = backgroundConfig?.jsonContent || {}

  const getAutoplay = () => {
    if (typeof window === 'undefined') return false
    const hash = window.location.hash.slice(1) // Remove the '#' prefix
    if (!hash) return false
    return (story?.config?.id || 'member-story') === hash ? true : false
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 w-screen ml-[calc((50%-50vw)-1%)] bg-[#f8f9fc] -z-10"></div>
      <Section
        className={'flex flex-col px-0 py-12 lg:py-16 lg:px-0 overflow-hidden'}
        contain={true}
        variant={'achieveHighlightBlue'}
        fullBackground={backgroundConfig?.jsonContent?.fullBackground ?? false}
        sectionStyles={backgroundConfig?.jsonContent?.style ?? null}
        data-testid="testimonial-tabs-section"
      >
        {(title || eyebrow) && (
          <TitleDivider
            disableTitleTag={true}
            eyebrow={eyebrow}
            eyebrowBlack
            title={title}
            eyebrowClassName={
              title
                ? 'tracking-[3.5px] leading-[16.8px] pb-2 md:pb-4'
                : 'tracking-[3.5px] leading-[16.8px] pb-[26px]'
            }
            classNameTitleText="lg:text-[32px] text-[24px] font-bold"
            className={eyebrow && !title ? 'mb-0' : ''}
            data-testid="testimonial-tabs-title"
          />
        )}

        {useVariantA ? null : (
          <div className="w-full relative mb-[32px] md:mb-[43px]">
            <div className="w-full flex justify-center items-center px-6 sm:px-0">
              <AchieveLink onClick={handlePrev}>
                <div className="md:hidden flex items-center h-full px-2">
                  <ChevronLeft size={20} className="mr-1" />
                  <div className="h-px flex-1 bg-[#E1E6F1]"></div>
                </div>
              </AchieveLink>

              <div className="w-full flex justify-center">
                <div className="flex sm:hidden justify-center w-full gap-[32px]">
                  {(selected === 0 || selected === 1 ? [0, 1] : [1, 2]).map((index) => (
                    <div
                      key={tabSvgs?.[index]?.sys?.id}
                      className="flex items-center justify-center cursor-pointer flex-1 min-w-[60px] max-w-[170px]"
                      onClick={() => handleClick(index)}
                    >
                      <MediaImageStandard
                        className="mr-[8px] ml-3"
                        content={tabSvgs[index].mediaContent}
                        layout="fixed"
                        height={23}
                        width={23}
                      />
                      <Typography
                        className="text-nowrap font-bold"
                        content={memberStories?.gridItemsCollection?.items?.[index].name}
                      />
                    </div>
                  ))}
                </div>

                <div className="hidden sm:flex justify-center w-full">
                  {tabs.map((index) => (
                    <div
                      key={tabSvgs?.[index]?.sys?.id}
                      className="flex items-center justify-center cursor-pointer flex-1 min-w-[60px] max-w-[170px] mx-2"
                      onClick={() => handleClick(index)}
                    >
                      <div className="flex flex-col">
                        <div
                          className="flex flex-row px-[16px]"
                          id={
                            memberStories?.gridItemsCollection?.items?.[index]?.config?.id ||
                            `member-story-${index}`
                          }
                        >
                          <MediaImageStandard
                            className="mr-[8px]"
                            content={tabSvgs[index].mediaContent}
                            layout="fixed"
                            height={23}
                            width={23}
                          />
                          <Typography
                            className="text-nowrap font-bold"
                            content={memberStories?.gridItemsCollection?.items?.[index].name}
                          />
                        </div>
                        <div>
                          <div
                            className={`w-full mt-[9px] border border-solid ${
                              selected === index ? 'border-[#3300FF]' : 'border-transparent'
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <AchieveLink onClick={handleNext}>
                <div className="md:hidden flex items-center h-full px-2">
                  <div className="h-px flex-1 bg-[#E1E6F1]"></div>
                  <ChevronRight size={20} className="ml-1" />
                </div>
              </AchieveLink>
            </div>

            <div className="flex justify-center h-[2px] mt-2">
              <div className="w-[20px] bg-[#E1E6F1] sm:hidden" />
              <div className="flex flex-1 justify-center sm:hidden">
                {[0, 1, 2].map((index) => {
                  const currentPair = selected === 0 || selected === 1 ? [0, 1] : [1, 2]
                  return (
                    <div
                      key={index}
                      className={`flex-1 transition-colors duration-300 md:min-w-[60px] md:max-w-[170px] ${
                        selected === index ? 'bg-[#3300FF]' : 'bg-[#E1E6F1]'
                      } ${!currentPair.includes(index) ? 'hidden' : ''}`}
                    />
                  )
                })}
              </div>
              <div className="w-[20px] bg-[#E1E6F1] sm:hidden" />
            </div>
          </div>
        )}

        {story && (
          <div className="flex lg:flex-row flex-col lg:text-left">
            <>
              <div className="flex justify-center pb-8 w-full" data-testid="testimonial-video">
                <VideoPlayer
                  aspectRatio={16 / 9}
                  frameClassName={'max-w-[636px] rounded'}
                  content={{
                    ...story.videoDialog.videoPlayer,
                    thumbnail: story.image,
                  }}
                  capWidth={640}
                  extraPadding={200}
                  drillPadding={true}
                  maxWidth={true}
                />
              </div>

              <div
                className="flex flex-col ml-0 md:ml-6 justify-center lg:text-left text-center"
                data-testid="testimonial-content-wrapper"
              >
                <Typography
                  content={story.title}
                  className={
                    useVariantA
                      ? 'text-nowrap md:mt-[-20px] mb-[20px] text-[18px] md:text-[24px] font-Ultramarine font-bold'
                      : 'text-nowrap md:mt-[-20px] mb-[20px] text-[18px] md:text-[24px] font-Ultramarine font-bold leading-[130%] text-[#2C47F6]'
                  }
                />
                <div
                  className={'[&_strong]:font-medium! max-w-[803px] pb-6 flex flex-col text-left'}
                  data-testid="testimonial-copy-container"
                >
                  {storyContent?.map((paragraph, index) => {
                    const value = paragraph?.content?.[0]?.value || ''
                    return (
                      <Typography
                        key={paragraph.uuid}
                        content={value}
                        className={
                          index === 0
                            ? 'text-[#1D252F] font-Ultramarine sm:text-[16px] sm:text-[18px] font-normal leading-[160%] mb-[20px]'
                            : 'font-Ultramarine text-[16px] font-medium leading-[120%] text-[#42546B]'
                        }
                      />
                    )
                  })}
                </div>
                {story?.statGrid && (
                  <div className={'w-full pb-8'}>
                    <StatGrid grid={story?.statGrid?.fields} />
                  </div>
                )}
                {story?.videoDialog && !useVariantA && (
                  <div>
                    <VideoDialog
                      className="self-start px-0!"
                      capWidth={true}
                      content={{
                        ...story?.videoDialog,
                        explicitTitle: (
                          <Typography
                            className="text-[18px] font-bold leading-[120%] tracking-[0.36px]"
                            content={`Watch ${story?.name}`}
                          />
                        ),
                      }}
                      trackOpen={{
                        nav_link_section: 'Testimonials',
                      }}
                      autoOpen={getAutoplay()}
                    />
                  </div>
                )}
                {story?.disclaimer && !useVariantA && (
                  <Typography
                    content={story?.disclaimer}
                    variant="bodySm"
                    className={
                      'text-xs sm:text-base text-center sm:text-left text-[#42546b] max-w-[803px] w-full pt-[24px]'
                    }
                  />
                )}
              </div>
            </>
          </div>
        )}
        {useVariantA && (
          <div className="w-full flex justify-center items-center">
            <div className="flex items-center gap-2 bg-[#5C708A4D] px-2 py-1 rounded-full">
              {tabs.map((index) => (
                <div
                  key={index}
                  onClick={() => handleClick(index)}
                  className={`w-[6px] h-[6px] rounded-full transition-all duration-300 cursor-pointer ${
                    selected === index ? 'bg-[#3300FF] scale-110' : 'bg-white hover:bg-[#C5CEE0]'
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </Section>
    </div>
  )
}

export default TestimonialTabs
